import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {FiActivity, FiAlertCircle, FiBookOpen, FiCheck, FiCopy, FiEdit, FiFileText, FiLoader, FiPlus, FiRefreshCw, FiSearch, FiShield, FiTrash2, FiUploadCloud, FiUserPlus, FiUsers, FiX} from 'react-icons/fi';
import {accountsApi, getErrorMessage, resourcesApi, tasksApi} from '../services/api';
import {copyTextToClipboard} from '../ExamPage/examShare';
import useAuthRole from '../hooks/useAuthRole';
import './OrganizationAccess.css';

const emptyOrganization = {name: '', slug: '', is_active: true};
const emptyCourse = {title: '', code: '', description: '', is_active: true};
const emptyStudent = {first_name: '', last_name: '', student_number: '', email: '', phone_number: '', password: '', confirm_password: ''};
const toArray = (value) => Array.isArray(value) ? value : value?.results || value?.items || [];
const displayName = (user = {}) => user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim()
    || user.email || user.student_number || user.phone_number || `کاربر ${user.id}`;
const primaryIdentifier = (user = {}) => user.email || user.student_number || user.phone_number || `شناسه ${user.id}`;
const organizationRoles = [{value: 'master', label: 'مدیر مجموعه'}];
const courseRoles = [{value: 'student', label: 'دانشجوی دوره'}, {value: 'teacher', label: 'استاد دوره'}];

function MemberPicker({scopeKey, loadOptions, roles, defaultRole, busy, onAdd}) {
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [role, setRole] = useState(defaultRole);
    const [loading, setLoading] = useState(false);
    const availableRoles = selected?.available_roles
        ? roles.filter((item) => selected.available_roles.includes(item.value))
        : roles;

    useEffect(() => {
        setQuery('');
        setOptions([]);
        setSelected(null);
        setRole(defaultRole);
    }, [defaultRole, scopeKey]);

    useEffect(() => {
        if (selected || query.trim().length < 2) {
            setOptions([]);
            return undefined;
        }
        let active = true;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await loadOptions(query.trim());
                if (active) setOptions(toArray(response));
            } finally {
                if (active) setLoading(false);
            }
        }, 350);
        return () => { active = false; clearTimeout(timer); };
    }, [loadOptions, query, selected]);

    const alreadyAssigned = selected?.current_roles?.includes(role);
    const submit = async (event) => {
        event.preventDefault();
        if (!selected || alreadyAssigned) return;
        await onAdd(selected.id, role);
        setSelected(null);
        setQuery('');
    };

    return <form className="oa-member-picker" onSubmit={submit}>
        <div className="oa-user-combobox">
            <label><FiSearch/><input value={query} onChange={(event) => { setQuery(event.target.value); setSelected(null); }} placeholder="نام، ایمیل، موبایل یا شماره دانشجویی..." aria-label="جستجوی کاربر" autoComplete="off"/></label>
            {loading && <FiLoader className="oa-picker-loader oa-spin"/>}
            {!selected && query.trim().length >= 2 && <div className="oa-picker-results">
                {options.map((option) => <button type="button" key={option.id} onClick={() => { const nextRoles = roles.filter((item) => option.available_roles?.includes(item.value)); setSelected(option); setRole(nextRoles[0]?.value || defaultRole); setQuery(displayName(option)); setOptions([]); }}>
                    <span className="oa-avatar">{displayName(option).slice(0, 1)}</span>
                    <span><strong>{displayName(option)}</strong><small>{primaryIdentifier(option)}</small></span>
                    {!!option.current_roles?.length && <em>{option.current_roles.map((item) => roles.find((entry) => entry.value === item)?.label || item).join('، ')}</em>}
                </button>)}
                {!loading && !options.length && <div className="oa-picker-empty">کاربری پیدا نشد.</div>}
            </div>}
            {query.trim().length === 1 && <small className="oa-picker-hint">حداقل دو نویسه وارد کنید.</small>}
        </div>
        <select value={role} onChange={(event) => setRole(event.target.value)} aria-label="نقش کاربر" disabled={!availableRoles.length}>{availableRoles.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <button type="submit" disabled={busy || !selected || !availableRoles.length || alreadyAssigned}><FiUserPlus/>{alreadyAssigned ? 'نقش ثبت شده' : 'افزودن'}</button>
    </form>;
}

const importStatusLabels = {
    ready: 'آماده افزودن', ready_create: 'حساب جدید', added: 'اضافه شد', created: 'حساب ساخته شد', already_member: 'عضو قبلی',
    not_found: 'حساب پیدا نشد', inactive: 'حساب غیرفعال', invalid: 'اطلاعات ناقص', duplicate: 'تکراری در فایل',
};

function StudentImportDialog({open, courseName, busy, result, file, onFile, onPreview, onCommit, onClose}) {
    if (!open) return null;
    const readyCount = (result?.summary?.ready || 0) + (result?.summary?.ready_create || 0);
    const addedCount = (result?.summary?.added || 0) + (result?.summary?.created || 0);
    return <div className="oa-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !busy && onClose()}>
        <section className="oa-import-dialog" role="dialog" aria-modal="true" aria-labelledby="student-import-title">
            <header><div><span><FiUploadCloud/> ورود گروهی دانشجو</span><h2 id="student-import-title">افزودن از فایل اکسل</h2><p>دوره مقصد: {courseName}</p></div><button type="button" onClick={onClose} disabled={busy} aria-label="بستن"><FiX/></button></header>
            {!result && <>
                <label className="oa-upload-box"><FiFileText/><strong>{file ? file.name : 'فایل اکسل دانشجویان را انتخاب کنید'}</strong><span>فقط .xlsx، حداکثر ۵ مگابایت و ۱۰۰۰ ردیف</span><input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => onFile(event.target.files?.[0] || null)}/></label>
                <div className="oa-template-guide"><strong>ساختار سطر اول فایل</strong><div><span>نام و نام خانوادگی</span><span>شماره دانشجویی</span></div><small>نمونه: امیرعلی خوارزمی — 40205264</small></div>
                <div className="oa-dialog-actions"><button type="button" className="secondary" onClick={onClose} disabled={busy}>انصراف</button><button type="button" onClick={onPreview} disabled={!file || busy}>{busy ? <FiLoader className="oa-spin"/> : <FiSearch/>}بررسی فایل</button></div>
            </>}
            {result && <>
                <div className="oa-import-summary"><article><b>{result.summary.total}</b><span>کل ردیف‌ها</span></article><article className="ok"><b>{readyCount || addedCount}</b><span>{result.preview ? 'آماده افزودن' : 'اضافه‌شده'}</span></article><article className="neutral"><b>{result.summary.already_member}</b><span>عضو قبلی</span></article><article className="error"><b>{result.summary.not_found + result.summary.invalid + result.summary.duplicate + (result.summary.inactive || 0)}</b><span>نیازمند اصلاح</span></article></div>
                <div className="oa-import-results"><div className="oa-import-head"><span>ردیف</span><span>نام دانشجو</span><span>شماره دانشجویی</span><span>نتیجه</span></div>{result.rows.map((row) => <article key={`${row.row}-${row.student_number}`}><span>{row.row}</span><strong>{row.full_name || '—'}</strong><code>{row.student_number || '—'}</code><div><em className={row.status}>{importStatusLabels[row.status] || row.status}</em>{row.warning && <small><FiAlertCircle/>{row.warning}</small>}{!row.warning && !['ready', 'ready_create', 'added', 'created'].includes(row.status) && <small>{row.detail}</small>}</div></article>)}</div>
                <div className="oa-dialog-actions"><button type="button" className="secondary" onClick={() => result.preview ? onFile(null) : onClose()} disabled={busy}>{result.preview ? 'انتخاب فایل دیگر' : 'بستن'}</button>{result.preview && <button type="button" onClick={onCommit} disabled={!readyCount || busy}>{busy ? <FiLoader className="oa-spin"/> : <FiCheck/>}افزودن {readyCount} دانشجو</button>}</div>
            </>}
        </section>
    </div>;
}

function StudentCreateDialog({open, courseName, busy, onSubmit, onClose, canViewLearnerState}) {
    const [form, setForm] = useState(emptyStudent);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(emptyStudent);
        setResult(null);
        setError('');
        setCopied(false);
    }, [courseName, open]);

    if (!open) return null;

    const update = (field) => (event) => {
        setForm((current) => ({...current, [field]: event.target.value}));
        setError('');
    };
    const submit = async (event) => {
        event.preventDefault();
        setError('');
        if (form.password !== form.confirm_password) {
            setError('رمز عبور و تکرار آن یکسان نیستند.');
            return;
        }
        if (form.password && form.password.length < 8) {
            setError('رمز عبور باید حداقل ۸ نویسه داشته باشد.');
            return;
        }
        try {
            setResult(await onSubmit({
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                student_number: form.student_number.trim(),
                email: form.email.trim(),
                phone_number: form.phone_number.trim(),
                password: form.password,
            }));
        } catch (submitError) {
            setError(getErrorMessage(submitError));
        }
    };
    const copyCredentials = async () => {
        const accountPassword = result?.temporary_password || form.password;
        if (!accountPassword) return;
        const text = `شماره دانشجویی: ${result.membership?.user?.student_number || form.student_number}\nرمز عبور: ${accountPassword}`;
        try {
            await copyTextToClipboard(text);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    return <div className="oa-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
        <section className="oa-import-dialog oa-student-dialog" role="dialog" aria-modal="true" aria-labelledby="single-student-title">
            <header><div><span><FiUserPlus/> افزودن تکی دانشجو</span><h2 id="single-student-title">{courseName}</h2><p>حساب موجود با شماره دانشجویی تطبیق داده می‌شود؛ در غیر این صورت حساب جدید ساخته خواهد شد.</p></div><button type="button" onClick={onClose} disabled={busy} aria-label="بستن"><FiX/></button></header>
            {result ? <div className={`oa-student-result ${result.account_created ? 'created' : 'existing'}`}>
                <FiCheck/>
                <div><strong>{result.account_created ? 'حساب ساخته و به دوره اضافه شد.' : result.membership_created ? 'حساب موجود به دوره اضافه شد.' : 'این دانشجو از قبل عضو دوره بود.'}</strong>
                    {result.account_created && <p>{result.password_was_generated ? 'رمز موقت فقط همین یک‌بار نمایش داده می‌شود؛ آن را امن در اختیار دانشجو قرار دهید.' : 'حساب با رمز انتخاب‌شده ساخته شد؛ اطلاعات ورود را امن در اختیار دانشجو قرار دهید.'}</p>}
                </div>
                {result.account_created && <div className="oa-credentials"><span>شماره دانشجویی <code>{result.membership?.user?.student_number || form.student_number}</code></span><span>{result.password_was_generated ? 'رمز موقت' : 'رمز عبور'} <code>{result.temporary_password || form.password}</code></span><button type="button" onClick={copyCredentials}><FiCopy/>{copied ? 'کپی شد' : 'کپی اطلاعات ورود'}</button></div>}
                {canViewLearnerState && result.membership?.user?.id != null && <Link className="oa-learner-state-link" to={`/learner-state/${encodeURIComponent(result.membership.user.id)}`}><FiActivity/> دیدن وضعیت یادگیری</Link>}
            </div> : <form className="oa-form oa-student-form" onSubmit={submit}>
                <label><span>نام</span><input required value={form.first_name} onChange={update('first_name')} autoFocus/></label>
                <label><span>نام خانوادگی</span><input required value={form.last_name} onChange={update('last_name')}/></label>
                <label><span>شماره دانشجویی</span><input required dir="ltr" inputMode="numeric" value={form.student_number} onChange={update('student_number')} placeholder="40211053"/></label>
                <label><span>شماره موبایل <small>اختیاری</small></span><input dir="ltr" inputMode="tel" value={form.phone_number} onChange={update('phone_number')} placeholder="0912..."/></label>
                <label className="wide"><span>پست الکترونیکی <small>اختیاری</small></span><input type="email" dir="ltr" value={form.email} onChange={update('email')} placeholder="student@example.com"/></label>
                <label><span>رمز عبور <small>اختیاری</small></span><input type="password" dir="ltr" minLength={8} autoComplete="new-password" value={form.password} onChange={update('password')} placeholder="حداقل ۸ نویسه"/><small>اگر خالی باشد، رمز امن به‌صورت خودکار ساخته می‌شود.</small></label>
                <label><span>تکرار رمز عبور</span><input type="password" dir="ltr" minLength={form.password ? 8 : undefined} required={Boolean(form.password)} autoComplete="new-password" value={form.confirm_password} onChange={update('confirm_password')} disabled={!form.password}/></label>
                {error && <div className="oa-inline-error wide"><FiAlertCircle/>{error}</div>}
                <div className="oa-dialog-actions wide"><button type="button" className="secondary" onClick={onClose} disabled={busy}>انصراف</button><button type="submit" disabled={busy}>{busy ? <FiLoader className="oa-spin"/> : <FiUserPlus/>}{busy ? 'در حال ایجاد…' : 'ایجاد و افزودن'}</button></div>
            </form>}
            {result && <div className="oa-dialog-actions"><button type="button" onClick={onClose}>بستن</button></div>}
        </section>
    </div>;
}

export default function OrganizationAccess() {
    const {isPlatformAdmin} = useAuthRole();
    const [searchParams] = useSearchParams();
    const requestedOrganizationId = searchParams.get('organization');
    const requestedCourseId = searchParams.get('course');
    const [organizations, setOrganizations] = useState([]);
    const [courses, setCourses] = useState([]);
    const [organizationMembers, setOrganizationMembers] = useState([]);
    const [courseMembers, setCourseMembers] = useState([]);
    const [courseResources, setCourseResources] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(false);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [organizationForm, setOrganizationForm] = useState(emptyOrganization);
    const [courseForm, setCourseForm] = useState(emptyCourse);
    const [activeSection, setActiveSection] = useState('members');
    const [courseView, setCourseView] = useState('members');
    const [memberSearch, setMemberSearch] = useState('');
    const [organizationMemberSearch, setOrganizationMemberSearch] = useState('');
    const [memberRoleFilter, setMemberRoleFilter] = useState('all');
    const [memberOrdering, setMemberOrdering] = useState('name');
    const [search, setSearch] = useState('');
    const [organizationQuery, setOrganizationQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [studentCreateOpen, setStudentCreateOpen] = useState(false);
    const [creatingStudent, setCreatingStudent] = useState(false);

    const selectedOrganization = organizations.find((item) => item.id === selectedOrganizationId) || null;
    const selectedCourse = courses.find((item) => item.id === selectedCourseId) || null;

    const loadOrganizations = useCallback(async () => {
        setLoading(true);
        try {
            setOrganizations(toArray(await accountsApi.organizations.list(organizationQuery ? {q: organizationQuery} : {})));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setLoading(false);
        }
    }, [organizationQuery]);

    const loadOrganizationDetails = useCallback(async (organizationId, canManageOrganization = false) => {
        if (!organizationId) {
            setCourses([]);
            setOrganizationMembers([]);
            return;
        }
        setDetailsLoading(true);
        try {
            const [memberItems, courseItems] = await Promise.all([
                canManageOrganization ? accountsApi.organizations.members(organizationId) : Promise.resolve([]),
                accountsApi.courses.list({organization: organizationId}),
            ]);
            setOrganizationMembers(toArray(memberItems));
            setCourses(toArray(courseItems));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setDetailsLoading(false);
        }
    }, []);

    const loadCourseMembers = useCallback(async (courseId) => {
        if (!courseId) {
            setCourseMembers([]);
            return;
        }
        try {
            setCourseMembers(toArray(await accountsApi.courses.members(courseId)));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        }
    }, []);

    useEffect(() => { loadOrganizations(); }, [loadOrganizations]);
    useEffect(() => {
        if (!requestedOrganizationId || selectedOrganizationId) return;
        const organization = organizations.find((item) => String(item.id) === requestedOrganizationId);
        if (organization) {
            setSelectedOrganizationId(organization.id);
            setOrganizationForm({name: organization.name || '', slug: organization.slug || '', is_active: organization.is_active !== false});
            setActiveSection('courses');
        }
    }, [organizations, requestedOrganizationId, selectedOrganizationId]);
    useEffect(() => {
        if (!requestedCourseId || selectedCourseId || !selectedOrganizationId) return;
        const course = courses.find((item) => String(item.id) === requestedCourseId);
        if (course) {
            setSelectedCourseId(course.id);
            setCourseForm({title: course.title || '', code: course.code || '', description: course.description || '', is_active: course.is_active !== false});
            setCourseView('resources');
        }
    }, [courses, requestedCourseId, selectedCourseId, selectedOrganizationId]);
    useEffect(() => {
        const timer = setTimeout(() => setOrganizationQuery(search.trim()), 350);
        return () => clearTimeout(timer);
    }, [search]);
    useEffect(() => { loadOrganizationDetails(selectedOrganizationId, Boolean(selectedOrganization?.can_manage)); }, [loadOrganizationDetails, selectedOrganization?.can_manage, selectedOrganizationId]);
    useEffect(() => { loadCourseMembers(selectedCourseId); }, [loadCourseMembers, selectedCourseId]);
    useEffect(() => {
        if (!selectedCourseId || courseView !== 'resources') return undefined;
        let active = true;
        setResourcesLoading(true);
        resourcesApi.listAll().then((response) => {
            if (active) setCourseResources(toArray(response).filter((resource) => String(resource.course_id) === String(selectedCourseId)));
        }).catch((error) => {
            if (active) setMessage({type: 'error', text: getErrorMessage(error)});
        }).finally(() => { if (active) setResourcesLoading(false); });
        return () => { active = false; };
    }, [courseView, selectedCourseId]);
    useEffect(() => {
        if (selectedOrganization && !selectedOrganization.can_manage && activeSection === 'members') setActiveSection('courses');
    }, [activeSection, selectedOrganization]);
    useEffect(() => {
        if (message?.type !== 'success') return undefined;
        const timer = setTimeout(() => setMessage(null), 3500);
        return () => clearTimeout(timer);
    }, [message]);

    const totals = useMemo(() => ({
        active: organizations.filter((item) => item.is_active).length,
        manageable: organizations.filter((item) => item.can_manage).length,
        masters: organizations.reduce((sum, item) => sum + (item.master_count || 0), 0),
    }), [organizations]);
    const organizationDirty = selectedOrganization ? (
        organizationForm.name.trim() !== selectedOrganization.name
        || organizationForm.is_active !== (selectedOrganization.is_active !== false)
    ) : Boolean(organizationForm.name.trim() || organizationForm.slug.trim());
    const courseDirty = selectedCourse ? (
        courseForm.title.trim() !== (selectedCourse.title || '')
        || courseForm.code.trim() !== (selectedCourse.code || '')
        || courseForm.description.trim() !== (selectedCourse.description || '')
        || courseForm.is_active !== (selectedCourse.is_active !== false)
    ) : Boolean(courseForm.title.trim() || courseForm.code.trim() || courseForm.description.trim());
    const visibleCourseMembers = useMemo(() => {
        const query = memberSearch.trim().toLocaleLowerCase('fa');
        const filtered = courseMembers.filter((membership) => {
            if (memberRoleFilter !== 'all' && membership.role !== memberRoleFilter) return false;
            if (!query) return true;
            const user = membership.user || {};
            return [displayName(user), user.email, user.student_number, user.phone_number]
                .filter(Boolean)
                .some((value) => String(value).toLocaleLowerCase('fa').includes(query));
        });
        return [...filtered].sort((first, second) => {
            if (memberOrdering === 'role') return String(first.role).localeCompare(String(second.role), 'fa');
            return displayName(first.user).localeCompare(displayName(second.user), 'fa');
        });
    }, [courseMembers, memberOrdering, memberRoleFilter, memberSearch]);
    const visibleOrganizationMembers = useMemo(() => {
        const query = organizationMemberSearch.trim().toLocaleLowerCase('fa');
        if (!query) return organizationMembers;
        return organizationMembers.filter((member) => (
            [displayName(member), member.email, member.student_number, member.phone_number]
                .filter(Boolean)
                .some((value) => String(value).toLocaleLowerCase('fa').includes(query))
        ));
    }, [organizationMemberSearch, organizationMembers]);
    useEffect(() => {
        if (!organizationDirty && !courseDirty) return undefined;
        const warnBeforeLeave = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeLeave);
        return () => window.removeEventListener('beforeunload', warnBeforeLeave);
    }, [courseDirty, organizationDirty]);
    const confirmDiscardChanges = () => (
        (!organizationDirty && !courseDirty)
        || window.confirm('تغییرات ذخیره‌نشده دارید. بدون ذخیره از این بخش خارج می‌شوید؟')
    );

    const selectOrganization = (organization) => {
        if (organization.id === selectedOrganizationId || !confirmDiscardChanges()) return;
        setSelectedOrganizationId(organization.id);
        setSelectedCourseId(null);
        setOrganizationForm({name: organization.name || '', slug: organization.slug || '', is_active: organization.is_active !== false});
        setCourseForm(emptyCourse);
        setActiveSection(organization.can_manage ? 'members' : 'courses');
        setMessage(null);
        setImportOpen(false);
        setImportFile(null);
        setImportResult(null);
        setStudentCreateOpen(false);
        setMemberSearch('');
        setOrganizationMemberSearch('');
        setMemberRoleFilter('all');
    };

    const startOrganization = () => {
        if ((!selectedOrganizationId && !organizationDirty && !courseDirty) || confirmDiscardChanges()) {
            setSelectedOrganizationId(null);
            setSelectedCourseId(null);
            setOrganizationForm(emptyOrganization);
            setCourseForm(emptyCourse);
            setCourses([]);
            setOrganizationMembers([]);
            setCourseMembers([]);
            setMessage(null);
        }
    };

    const saveOrganization = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            const payload = {name: organizationForm.name.trim(), is_active: organizationForm.is_active};
            if (!selectedOrganizationId) payload.slug = organizationForm.slug.trim().toLowerCase();
            const saved = selectedOrganizationId
                ? await accountsApi.organizations.update(selectedOrganizationId, payload)
                : await accountsApi.organizations.create(payload);
            setSelectedOrganizationId(saved.id);
            setOrganizationForm({name: saved.name, slug: saved.slug, is_active: saved.is_active !== false});
            setMessage({type: 'success', text: selectedOrganizationId ? 'مشخصات مجموعه ذخیره شد.' : 'مجموعه و گروه‌های دسترسی آن ساخته شد.'});
            await loadOrganizations();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setSaving(false);
        }
    };

    const removeOrganization = async (organization) => {
        if (window.prompt(`برای حذف دائمی مجموعه، عبارت «${organization.name}» را وارد کنید. غیرفعال‌سازی انتخاب امن‌تری است.`) !== organization.name) return;
        setSaving(true);
        try {
            await accountsApi.organizations.remove(organization.id);
            if (selectedOrganizationId === organization.id) startOrganization();
            setMessage({type: 'success', text: 'مجموعه حذف شد.'});
            await loadOrganizations();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setSaving(false);
        }
    };

    const mutateOrganizationMember = async (action, payload, successText) => {
        if (!selectedOrganizationId) return;
        setSaving(true);
        setMessage(null);
        try {
            await action(selectedOrganizationId, payload);
            setMessage({type: 'success', text: successText});
            await Promise.all([loadOrganizationDetails(selectedOrganizationId, true), loadOrganizations()]);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setSaving(false);
        }
    };

    const changeImportFile = (file) => {
        setImportFile(file);
        setImportResult(null);
    };

    const waitForImport = async (taskId) => {
        for (let attempt = 0; attempt < 60; attempt += 1) {
            const task = await tasksApi.detail(taskId);
            if (task.state === 'FAILURE') throw new Error(task.error || 'پردازش فایل ناموفق بود.');
            if (task.state === 'SUCCESS') {
                if (typeof task.result !== 'string') return task.result;
                try { return JSON.parse(task.result); }
                catch { throw new Error('گزارش پردازش فایل نامعتبر است.'); }
            }
            await new Promise((resolve) => setTimeout(resolve, 1500));
        }
        throw new Error('پردازش فایل بیش از حد انتظار طول کشید.');
    };

    const runStudentImport = async (commit) => {
        if (!selectedCourseId || !importFile) return;
        setImporting(true);
        setMessage(null);
        try {
            const queued = await accountsApi.courses.importStudents(selectedCourseId, importFile, commit);
            const result = queued?.task_id ? await waitForImport(queued.task_id) : queued;
            setImportResult(result);
            if (commit) {
                const count = (result.summary?.added || 0) + (result.summary?.created || 0);
                setMessage({type: 'success', text: `${count} دانشجو به دوره اضافه شد. حساب‌های جدید باید در اولین ورود رمز خود را تغییر دهند.`});
                await Promise.all([loadCourseMembers(selectedCourseId), loadOrganizationDetails(selectedOrganizationId, Boolean(selectedOrganization?.can_manage)), loadOrganizations()]);
            }
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setImporting(false);
        }
    };

    const createStudent = async (payload) => {
        if (!selectedCourseId) return null;
        setCreatingStudent(true);
        setMessage(null);
        try {
            const result = await accountsApi.courses.createStudent(selectedCourseId, payload);
            const successText = result.account_created
                ? 'حساب دانشجو ساخته و به دوره اضافه شد.'
                : result.membership_created ? 'حساب موجود به دوره اضافه شد.' : 'دانشجو از قبل عضو دوره بود.';
            setMessage({type: 'success', text: successText});
            await Promise.all([loadCourseMembers(selectedCourseId), loadOrganizationDetails(selectedOrganizationId, Boolean(selectedOrganization?.can_manage)), loadOrganizations()]);
            return result;
        } finally {
            setCreatingStudent(false);
        }
    };

    const selectCourse = (course) => {
        if (course.id === selectedCourseId || !confirmDiscardChanges()) return;
        setSelectedCourseId(course.id);
        setCourseForm({title: course.title || '', code: course.code || '', description: course.description || '', is_active: course.is_active !== false});
        setImportOpen(false);
        setImportFile(null);
        setImportResult(null);
        setStudentCreateOpen(false);
        setCourseView('members');
        setMemberSearch('');
        setMemberRoleFilter('all');
    };

    const startCourse = () => {
        if ((!selectedCourseId && !courseDirty) || confirmDiscardChanges()) {
            setSelectedCourseId(null);
            setCourseForm(emptyCourse);
            setCourseMembers([]);
            setCourseView('settings');
            setMemberSearch('');
            setMemberRoleFilter('all');
        }
    };

    const changeSection = (section) => {
        if (section === activeSection) return;
        if (!confirmDiscardChanges()) return;
        setActiveSection(section);
    };

    const saveCourse = async (event) => {
        event.preventDefault();
        if (!selectedOrganizationId) return;
        setSaving(true);
        setMessage(null);
        try {
            const payload = {
                organization_id: selectedOrganizationId,
                title: courseForm.title.trim(),
                code: courseForm.code.trim(),
                description: courseForm.description.trim(),
                is_active: courseForm.is_active,
            };
            const saved = selectedCourseId
                ? await accountsApi.courses.update(selectedCourseId, payload)
                : await accountsApi.courses.create(payload);
            setSelectedCourseId(saved.id);
            setCourseForm({title: saved.title, code: saved.code, description: saved.description || '', is_active: saved.is_active !== false});
            setMessage({type: 'success', text: selectedCourseId ? 'دوره بروزرسانی شد.' : 'دوره جدید ساخته شد.'});
            await loadOrganizationDetails(selectedOrganizationId, Boolean(selectedOrganization?.can_manage));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setSaving(false);
        }
    };

    const removeCourse = async (course) => {
        if (window.prompt(`برای حذف دائمی دوره، عبارت «${course.title}» را وارد کنید.`) !== course.title) return;
        setSaving(true);
        try {
            await accountsApi.courses.remove(course.id);
            if (selectedCourseId === course.id) startCourse();
            setMessage({type: 'success', text: 'دوره حذف شد.'});
            await loadOrganizationDetails(selectedOrganizationId, Boolean(selectedOrganization?.can_manage));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setSaving(false);
        }
    };

    const mutateCourseMember = async (action, payload, successText) => {
        if (!selectedCourseId) return;
        setSaving(true);
        setMessage(null);
        try {
            await action(selectedCourseId, payload);
            setMessage({type: 'success', text: successText});
            await Promise.all([loadCourseMembers(selectedCourseId), loadOrganizationDetails(selectedOrganizationId, Boolean(selectedOrganization?.can_manage))]);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setSaving(false);
        }
    };

    const searchOrganizationMembers = useCallback(
        (query) => accountsApi.organizations.memberOptions(selectedOrganizationId, {q: query}),
        [selectedOrganizationId],
    );
    const searchCourseMembers = useCallback(
        (query) => accountsApi.courses.memberOptions(selectedCourseId, {q: query}),
        [selectedCourseId],
    );

    const canManageCourseStructure = selectedCourseId
        ? Boolean(selectedCourse?.can_manage_structure)
        : Boolean(selectedOrganization?.can_manage);
    const canManageStudents = Boolean(selectedCourse?.can_manage_students);
    const canManageTeachers = Boolean(selectedCourse?.can_manage_teachers);
    const manageableCourseRoles = courseRoles.filter((item) => (
        item.value === 'student' ? canManageStudents : canManageTeachers
    ));
    const accessLabel = isPlatformAdmin
        ? 'مدیر سامانه'
        : selectedOrganization?.can_manage ? 'مدیر سازمان' : 'استاد دوره';

    return <main className="oa-page">
        <section className="oa-hero app-page-header">
            <div><span><FiShield/> کنترل دسترسی سازمانی</span><h1>مجموعه‌ها و دسترسی‌ها</h1><p>عضویت سازمانی، دوره‌ها و دسترسی محتوای آموزشی را از یک نقطه مدیریت کنید.</p></div>
            {isPlatformAdmin && <button type="button" onClick={startOrganization}><FiPlus/> مجموعه جدید</button>}
        </section>

        <section className="oa-stats" aria-label="خلاصه دسترسی‌ها">
            <span><strong>{organizations.length}</strong> مجموعه</span>
            <span><strong>{totals.active}</strong> فعال</span>
            <span><strong>{totals.manageable}</strong> قابل مدیریت</span>
            <span><strong>{totals.masters}</strong> مدیر سازمان</span>
        </section>
        {message && <div role="status" className={`oa-message ${message.type}`}>{message.text}<button type="button" onClick={() => setMessage(null)} aria-label="بستن پیام"><FiX/></button></div>}

        <section className="oa-mobile-context" aria-label="انتخاب مجموعه">
            <label><span>مجموعه فعال</span><select value={selectedOrganizationId || ''} onChange={(event) => {
                const organization = organizations.find((item) => String(item.id) === event.target.value);
                if (organization) selectOrganization(organization);
                else if (isPlatformAdmin) startOrganization();
            }}><option value="">{isPlatformAdmin ? 'ساخت مجموعه جدید' : 'انتخاب مجموعه'}</option>{organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}</select></label>
            {isPlatformAdmin && <button type="button" onClick={startOrganization}><FiPlus/> جدید</button>}
        </section>

        <div className="oa-layout">
            <section className="oa-panel oa-directory">
                <header><div><h2>مجموعه‌ها</h2><p>{organizations.length} مورد قابل مشاهده</p></div><button type="button" aria-label="بروزرسانی" onClick={loadOrganizations} disabled={loading}><FiRefreshCw className={loading ? 'oa-spin' : ''}/></button></header>
                <label className="oa-search"><FiSearch/><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="جستجو در نام یا شناسه..."/></label>
                {loading ? <div className="oa-empty"><FiLoader className="oa-spin"/> در حال دریافت...</div> : <div className="oa-list">
                    {organizations.map((organization) => <article key={organization.id} className={selectedOrganizationId === organization.id ? 'active' : ''}>
                        <button type="button" onClick={() => selectOrganization(organization)}><strong>{organization.name}</strong><span dir="ltr">{organization.slug}</span></button>
                        <div><small className={organization.is_active ? 'enabled' : 'disabled'}>{organization.is_active ? 'فعال' : 'غیرفعال'}</small><small>{organization.student_count || 0} دانشجو</small><small>{organization.teacher_count || 0} استاد</small><small>{organization.master_count || 0} مدیر</small></div>
                    </article>)}
                    {!organizations.length && <div className="oa-empty">مجموعه‌ای پیدا نشد.</div>}
                </div>}
            </section>

            <section className="oa-panel oa-workspace">
                {!selectedOrganization && isPlatformAdmin ? <>
                    <header><div><h2>ساخت مجموعه</h2><p>پس از ساخت، گروه مدیران و دانشجویان به‌صورت خودکار ایجاد می‌شود.</p></div></header>
                    <form className="oa-form oa-create-form" onSubmit={saveOrganization}>
                        <label><span>نام مجموعه</span><input autoFocus required value={organizationForm.name} onChange={(event) => setOrganizationForm({...organizationForm, name: event.target.value})} placeholder="مثلاً دانشکده فنی"/></label>
                        <label><span>شناسه لاتین</span><input required value={organizationForm.slug} onChange={(event) => setOrganizationForm({...organizationForm, slug: event.target.value.replace(/\s+/g, '-').toLowerCase()})} pattern="[a-z0-9-]+" placeholder="engineering"/><small>بعداً قابل تغییر نیست.</small></label>
                        <button type="submit" disabled={saving || !organizationDirty || !organizationForm.name.trim() || !organizationForm.slug.trim()}>{saving ? <FiLoader className="oa-spin"/> : <FiCheck/>}ساخت مجموعه</button>
                    </form>
                </> : !selectedOrganization ? <div className="oa-empty">سازمان قابل مدیریتی برای شما وجود ندارد.</div> : <>
                    <header className="oa-workspace-header"><div><span className={`oa-status-dot ${selectedOrganization.is_active ? 'active' : ''}`}/><div><div className="oa-breadcrumb"><span>مجموعه‌ها</span><b>/</b><strong>{selectedOrganization.name}</strong></div><h2>{selectedOrganization.name}</h2><p dir="ltr">{selectedOrganization.slug}</p></div></div><span className="oa-access-badge"><FiShield/>{accessLabel}</span></header>
                    <div className="oa-access-summary"><span><FiShield/> گروه مدیران سازمان <b>{selectedOrganization.master_group_name}</b></span><span>وضعیت <strong>{selectedOrganization.is_active ? 'فعال' : 'غیرفعال'}</strong></span></div>
                    <nav className="oa-tabs" aria-label="مدیریت مجموعه" role="tablist">
                        {selectedOrganization.can_manage && <button type="button" role="tab" aria-selected={activeSection === 'members'} className={activeSection === 'members' ? 'active' : ''} onClick={() => changeSection('members')}><FiUsers/> مدیران سازمان <b>{organizationMembers.length}</b></button>}
                        <button type="button" role="tab" aria-selected={activeSection === 'courses'} className={activeSection === 'courses' ? 'active' : ''} onClick={() => changeSection('courses')}><FiBookOpen/> دوره‌ها <b>{courses.length}</b></button>
                        {selectedOrganization.can_manage && <button type="button" role="tab" aria-selected={activeSection === 'settings'} className={activeSection === 'settings' ? 'active' : ''} onClick={() => changeSection('settings')}><FiEdit/> تنظیمات</button>}
                    </nav>

                    {detailsLoading ? <div className="oa-empty"><FiLoader className="oa-spin"/> در حال همگام‌سازی...</div> : activeSection === 'members' ? <section className="oa-members">
                        <div className="oa-section-heading"><div><h3>مدیران سازمان</h3><p>در سطح سازمان فقط نقش مدیر قابل افزودن یا حذف است.</p></div></div>
                        <div className="oa-member-add-panel"><div className="oa-member-add-copy"><strong>افزودن مدیر موجود</strong><span>کاربر را جست‌وجو و سپس نقش او را ثبت کنید.</span></div><MemberPicker scopeKey={selectedOrganizationId} loadOptions={searchOrganizationMembers} roles={organizationRoles} defaultRole="master" busy={saving} onAdd={(userId, role) => mutateOrganizationMember(accountsApi.organizations.addMember, {user_id: userId, role}, 'مدیر سازمان اضافه شد.')}/></div>
                        <div className="oa-member-toolbar single"><label><FiSearch/><input value={organizationMemberSearch} onChange={(event) => setOrganizationMemberSearch(event.target.value)} placeholder="جستجو در مدیران سازمان..."/></label><span>{visibleOrganizationMembers.length} نتیجه</span></div>
                        <div className="oa-member-table">
                            <div className="oa-table-head"><span>کاربر</span><span>شناسه ورود</span><span>نقش‌ها</span></div>
                            {visibleOrganizationMembers.map((member) => <article key={member.id}><div className="oa-member-identity"><span className="oa-avatar">{displayName(member).slice(0, 1)}</span><strong>{displayName(member)}</strong></div><span>{primaryIdentifier(member)}</span><div>{(member.roles || []).map((role) => {
                                const label = 'مدیر مجموعه';
                                const canRemove = selectedOrganization.can_manage;
                                return canRemove ? <button key={role} type="button" title="حذف این نقش" onClick={() => mutateOrganizationMember(accountsApi.organizations.removeMember, {user_id: member.id, role}, 'نقش سازمانی حذف شد.')}><span className={`oa-role ${role}`}>{label}</span><FiX/></button> : <span key={role} className={`oa-role oa-role-readonly ${role}`}>{label}</span>;
                            })}</div></article>)}
                            {!organizationMembers.length ? <div className="oa-empty">هنوز عضوی اضافه نشده است.</div> : !visibleOrganizationMembers.length && <div className="oa-empty">مدیری مطابق جستجو پیدا نشد.</div>}
                        </div>
                    </section> : activeSection === 'courses' ? <section className="oa-courses">
                        <div className="oa-course-switcher">
                            <label><span>دوره فعال</span><div><FiBookOpen/><select value={selectedCourseId || ''} onChange={(event) => {
                                const course = courses.find((item) => String(item.id) === event.target.value);
                                if (course) selectCourse(course);
                            }}><option value="" disabled>{selectedCourseId ? 'انتخاب دوره…' : selectedOrganization.can_manage ? 'دوره جدید' : 'انتخاب دوره'}</option>{courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}</select></div></label>
                            <div className="oa-course-switcher-summary"><span><strong>{courses.length}</strong> دوره</span>{selectedCourseId && <><span><strong>{selectedCourse?.student_count || 0}</strong> دانشجو</span><span><strong>{selectedCourse?.teacher_count || 0}</strong> استاد</span></>}</div>
                            {selectedOrganization.can_manage && <button type="button" onClick={startCourse}><FiPlus/> دوره جدید</button>}
                        </div>
                        <div className="oa-course-editor">
                            {!selectedCourseId && !selectedOrganization.can_manage ? <div className="oa-empty">برای مشاهده اعضا، یکی از دوره‌های خود را انتخاب کنید.</div> : <>
                            <div className="oa-course-context"><div><h3>{selectedCourseId ? selectedCourse?.title || 'دوره' : 'دوره جدید'}</h3>{selectedCourseId && <span>{selectedCourse?.code || 'بدون کد'} · {selectedCourse?.is_active === false ? 'غیرفعال' : 'فعال'}</span>}</div>{courseDirty && <em>تغییرات ذخیره‌نشده</em>}</div>
                            {selectedCourseId && <nav className="oa-course-tabs" aria-label="بخش‌های دوره" role="tablist"><button type="button" role="tab" aria-selected={courseView === 'members'} className={courseView === 'members' ? 'active' : ''} onClick={() => setCourseView('members')}><FiUsers/> اعضای دوره <b>{courseMembers.length}</b></button><button type="button" role="tab" aria-selected={courseView === 'resources'} className={courseView === 'resources' ? 'active' : ''} onClick={() => setCourseView('resources')}><FiFileText/> منابع دوره</button><button type="button" role="tab" aria-selected={courseView === 'settings'} className={courseView === 'settings' ? 'active' : ''} onClick={() => setCourseView('settings')}><FiEdit/> اطلاعات دوره</button></nav>}
                            {selectedCourseId && courseView === 'resources' && <section className="oa-course-resources">
                                <div className="oa-course-resources-heading"><div><h3>منابع این دوره</h3><p>فایل‌های آموزشی متصل به {selectedCourse?.title}</p></div><div>
                                    <Link to={`/manageresources?course=${encodeURIComponent(selectedCourseId)}`}>کتابخانه دوره</Link>
                                    {selectedCourse?.can_manage_content && selectedCourse?.is_active !== false && <Link className="primary" to={`/manageresources?course=${encodeURIComponent(selectedCourseId)}&upload=1`}><FiPlus/> افزودن منبع</Link>}
                                </div></div>
                                {resourcesLoading ? <div className="oa-empty"><FiLoader className="oa-spin"/> در حال دریافت منابع...</div> : courseResources.length ? <div className="oa-course-resource-list">{courseResources.map((resource) => <article key={resource.resource_id}><div><strong>{resource.title || resource.original_name}</strong><small>{resource.subject_title || resource.subject_type || 'منبع آموزشی'} · {resource.ingest_status === 'ingested' ? 'آماده' : resource.ingest_status === 'failed' ? 'نیازمند بررسی' : 'در حال آماده‌سازی'}</small></div><Link to={`/manageresources?course=${encodeURIComponent(selectedCourseId)}`}>مشاهده در کتابخانه</Link></article>)}</div> : <div className="oa-empty">هنوز منبعی به این دوره متصل نیست.</div>}
                            </section>}
                            {(!selectedCourseId || courseView === 'settings') && <form className="oa-form oa-course-form" onSubmit={saveCourse}>
                                <label><span>عنوان دوره</span><input required disabled={!canManageCourseStructure} value={courseForm.title} onChange={(event) => setCourseForm({...courseForm, title: event.target.value})} placeholder="الگوریتم‌ها"/></label>
                                <label><span>کد دوره</span><input required disabled={!canManageCourseStructure} value={courseForm.code} onChange={(event) => setCourseForm({...courseForm, code: event.target.value})} placeholder="CS-201"/></label>
                                <label className="wide"><span>توضیحات</span><textarea rows="2" disabled={!canManageCourseStructure} value={courseForm.description} onChange={(event) => setCourseForm({...courseForm, description: event.target.value})}/></label>
                                <label className="oa-check"><input type="checkbox" disabled={!canManageCourseStructure} checked={courseForm.is_active} onChange={(event) => setCourseForm({...courseForm, is_active: event.target.checked})}/><span>دوره فعال باشد</span></label>
                                {canManageCourseStructure && <button type="submit" disabled={saving || !courseDirty || !courseForm.title.trim() || !courseForm.code.trim()}>{saving ? <FiLoader className="oa-spin"/> : <FiCheck/>}{selectedCourseId ? 'ذخیره تغییرات' : 'ساخت دوره'}</button>}
                            </form>}
                            {selectedCourseId && courseView === 'members' && <div className="oa-course-members">
                                <div className="oa-course-members-heading"><div><h3>اعضای دوره</h3><p>نقش‌های قابل انتخاب براساس مجوز همین دوره از سرور دریافت می‌شوند.</p></div><div className="oa-course-member-actions">{isPlatformAdmin && <Link className="oa-learner-monitor-link" to={`/course-learner-state/${encodeURIComponent(selectedCourseId)}`}><FiActivity/> پایش یادگیرندگان</Link>}{canManageStudents && <><button type="button" onClick={() => setStudentCreateOpen(true)}><FiUserPlus/> افزودن تکی</button><button type="button" onClick={() => setImportOpen(true)}><FiUploadCloud/> ورود از اکسل</button></>}</div></div>
                                {!!manageableCourseRoles.length && <div className="oa-member-add-panel"><div className="oa-member-add-copy"><strong>افزودن حساب موجود</strong><span>ابتدا کاربر را پیدا کنید؛ دکمه افزودن پس از انتخاب فعال می‌شود.</span></div><MemberPicker scopeKey={selectedCourseId} loadOptions={searchCourseMembers} roles={manageableCourseRoles} defaultRole={manageableCourseRoles[0].value} busy={saving} onAdd={(userId, role) => mutateCourseMember(accountsApi.courses.addMember, {user_id: userId, role}, 'عضو به دوره اضافه شد.')}/></div>}
                                <div className="oa-member-toolbar"><label><FiSearch/><input value={memberSearch} onChange={(event) => setMemberSearch(event.target.value)} placeholder="جستجو در اعضای دوره..."/></label><select value={memberRoleFilter} onChange={(event) => setMemberRoleFilter(event.target.value)} aria-label="فیلتر نقش"><option value="all">همه نقش‌ها</option><option value="student">دانشجوها</option><option value="teacher">استادها</option></select><select value={memberOrdering} onChange={(event) => setMemberOrdering(event.target.value)} aria-label="مرتب‌سازی اعضا"><option value="name">مرتب‌سازی نام</option><option value="role">مرتب‌سازی نقش</option></select><span>{visibleCourseMembers.length} نتیجه</span></div>
                                <div className="oa-member-table compact">
                                    <div className="oa-table-head"><span>کاربر</span><span>شناسه ورود</span><span>نقش</span></div>
                                    {visibleCourseMembers.map((membership) => { const canRemove = membership.role === 'teacher' ? canManageTeachers : canManageStudents; const badge = <span className={`oa-role ${membership.role}`}>{membership.role === 'teacher' ? 'استاد دوره' : 'دانشجوی دوره'}</span>; return <article key={membership.id}><div className="oa-member-identity"><span className="oa-avatar">{displayName(membership.user).slice(0, 1)}</span><strong>{displayName(membership.user)}</strong></div><span>{primaryIdentifier(membership.user)}</span><div>{canRemove ? <button type="button" title="حذف از دوره" onClick={() => mutateCourseMember(accountsApi.courses.removeMember, {user_id: membership.user.id, role: membership.role}, 'عضویت دوره حذف شد.')}>{badge}<FiX/></button> : badge}{isPlatformAdmin && membership.role === 'student' && membership.user?.id != null && <Link className="oa-learner-state-link" to={`/learner-state/${encodeURIComponent(membership.user.id)}`} aria-label={`دیدن وضعیت یادگیری ${displayName(membership.user)}`}><FiActivity/> وضعیت یادگیری</Link>}</div></article>; })}
                                    {!courseMembers.length ? <div className="oa-empty">عضوی در این دوره ثبت نشده است.</div> : !visibleCourseMembers.length && <div className="oa-empty">عضوی مطابق جستجو یا فیلتر پیدا نشد.</div>}
                                </div>
                            </div>}
                            {selectedCourseId && courseView === 'settings' && canManageCourseStructure && <div className="oa-course-danger"><div><strong>حذف دائمی دوره</strong><p>عضویت‌ها و ارتباطات این دوره تحت تأثیر قرار می‌گیرند.</p></div><button type="button" className="oa-danger-link" onClick={() => removeCourse(selectedCourse)}><FiTrash2/> حذف دوره</button></div>}
                            </>}
                        </div>
                    </section> : <section className="oa-settings">
                        <div className="oa-section-heading"><div><h3>تنظیمات مجموعه</h3><p>شناسه لاتین برای حفظ ارتباط گروه‌ها قابل تغییر نیست.</p></div></div>
                        <form className="oa-form oa-settings-form" onSubmit={saveOrganization}>
                            <label><span>نام مجموعه</span><input required value={organizationForm.name} onChange={(event) => setOrganizationForm({...organizationForm, name: event.target.value})}/></label>
                            <label><span>شناسه لاتین</span><input disabled value={organizationForm.slug}/></label>
                            <label className="oa-check"><input type="checkbox" checked={organizationForm.is_active} onChange={(event) => setOrganizationForm({...organizationForm, is_active: event.target.checked})}/><span>مجموعه فعال و قابل انتخاب باشد</span></label>
                            <button type="submit" disabled={saving || !organizationDirty || !organizationForm.name.trim()}>{saving ? <FiLoader className="oa-spin"/> : <FiCheck/>}ذخیره تغییرات</button>
                        </form>
                        <div className="oa-danger-zone"><div><strong>حذف دائمی مجموعه</strong><p>برای داده‌های واقعی، غیرفعال‌سازی انتخاب امن‌تری است.</p></div><button type="button" onClick={() => removeOrganization(selectedOrganization)}><FiTrash2/> حذف مجموعه</button></div>
                    </section>}
                </>}
            </section>
        </div>
        <StudentImportDialog open={importOpen} courseName={selectedCourse?.title || ''} busy={importing} file={importFile} result={importResult} onFile={changeImportFile} onPreview={() => runStudentImport(false)} onCommit={() => runStudentImport(true)} onClose={() => { setImportOpen(false); setImportFile(null); setImportResult(null); }}/>
        <StudentCreateDialog open={studentCreateOpen} courseName={selectedCourse?.title || ''} busy={creatingStudent} onSubmit={createStudent} onClose={() => setStudentCreateOpen(false)} canViewLearnerState={isPlatformAdmin}/>
    </main>;
}
